import bbox from "@turf/bbox";
import { union, type AllGeoJSON } from "@turf/turf";
import {
  FeatureCollection,
  Feature as FeatureGeoJSON,
  MultiPolygon,
  Polygon,
} from "geojson";
import type { Map } from "mapbox-gl";
import { MapRef } from "react-map-gl";
import {
  Feature,
  FieldWithCurrentBoundary,
  MyUser,
  UserGet,
} from "regen-api-ts-client";

import { FieldBoundary } from "../components/FieldInfoMap/types";
import { AppRoleType, PermissionType } from "../types";
import { FormErrors, FormValue } from "./hooks";

export function isOnboarding() {
  return window.location.pathname.includes("/onboarding");
}

export function filterNullUndefined<TValue>(
  value: TValue | null | undefined,
): value is TValue {
  return value !== null && value !== undefined;
}

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const isValidEmail = (email: string) => {
  return EMAIL_REGEX.test(email);
};

const ZIPCODE_REGEX = /^\d{5}(-\d{4})?$/;
export const isValidZipCode = (zip: string) => {
  return ZIPCODE_REGEX.test(zip);
};

const LAT_LON_REGEX =
  /^\s*([+-]?\d+\.\d+)\s?([NnSs]?)\s*[,\s]\s*([+-]?\d+\.\d+)\s?([EeWw])?\s*$/;
export const detectLatLon = (text: string) => {
  const match = text.match(LAT_LON_REGEX);
  if (match) {
    let latModifier = 1;
    let lonModifier = 1;
    if (match[2] && match[2].toUpperCase() == "S") {
      latModifier = -1;
    }
    if (match[4] && match[4].toUpperCase() == "W") {
      lonModifier = -1;
    }
    return {
      lat: parseFloat(match[1]) * latModifier,
      lon: parseFloat(match[3]) * lonModifier,
    };
  }
};

// Use [number, number, number, number] instead of LngLatBoundsLike or BBox for
// best compatibility with both Mapbox and GeoJSON
// Turf will use feature's bbox if present, otherwise calculate it
export const calculateBBox = (
  features: AllGeoJSON | undefined,
): [number, number, number, number] | undefined => {
  if (!features) {
    return;
  }
  const boundingBox = bbox(features, { recompute: false });
  // Turf may return [Infinity, Infinity, -Infinity, -Infinity] or
  // [NaN, NaN, NaN, NaN] if the feature is malformed or empty
  if (boundingBox?.length >= 4 && boundingBox.every(Number.isFinite)) {
    return boundingBox.slice(0, 4) as [number, number, number, number];
  }
};

// Wrapper function to catch errors coming out of mapbox when bounds
// are not able to be calculated from current camera projection -
// temp so that we can figure out what's going on.
export const fitBoundsWrapper = (
  map?: MapRef | Map,
  bbox?: [number, number, number, number],
  options?: { padding?: number },
) => {
  try {
    if (map && bbox) {
      map.fitBounds(bbox, options);
    }
  } catch (e) {
    console.error("Failed to fit bounds", e, "Bounds sent", bbox);
  }
};

export const createNoopAsyncFunction = <T, R>(
  responseFunction: (args: T) => R,
  delay: number = 1000,
): ((args: T) => Promise<R>) => {
  return (args: T) => {
    return new Promise<R>((resolve) => {
      setTimeout(() => {
        resolve(responseFunction(args));
      }, delay);
    });
  };
};

export const getErodibilityKfactorLabel = (value: number) => {
  if (value < 0.15) {
    return "Low";
  } else if (value > 0.35) {
    return "High";
  }
  return "Moderate";
};

export const createNumberColorGradientMap = (
  values: number[],
  colors: string[],
) => {
  if (values.length !== colors.length) {
    return null;
  }
  return values.reduce(
    (acc, val, idx) => {
      acc[val] = colors[idx];
      return acc;
    },
    {} as { [key: string]: string },
  );
};

export const findClosestValue = (arr: number[], target: number) => {
  let closest = arr[0];
  let smallestDifference = Math.abs(target - closest);

  for (let i = 1; i < arr.length; i++) {
    const difference = Math.abs(target - arr[i]);
    if (difference < smallestDifference) {
      smallestDifference = difference;
      closest = arr[i];
    }
  }

  return closest;
};

export const validateEmail = (email: string) => {
  if (!email) {
    return "Email is required";
  }
  if (!isValidEmail(email)) {
    return "Please enter a valid email.";
  }

  return undefined;
};

export const validateZipCode = (zipCode: string) => {
  if (!zipCode) {
    return "ZIP code is required";
  }
  if (!isValidZipCode(zipCode)) {
    return "Please enter a valid ZIP code.";
  }

  return undefined;
};

export const validateGoals = (goals: string[]) => {
  if (goals.length === 0) {
    return "Please select at least one goal for your farm.";
  }

  return undefined;
};

export const validatePositiveNumber = (value: number, label: string) => {
  if (!value) {
    return `${label} is required`;
  }

  if (value < 0) {
    return `${label} cannot be negative`;
  }

  return undefined;
};

export const validateAcres = (acres: number) => {
  if (+acres > 1e6) {
    return "Acres cannot be greater than 1M";
  }
  return validatePositiveNumber(acres, "Acres");
};

export const validateYearsOperated = (years: number) => {
  return validatePositiveNumber(years, "Years operated");
};

export const hasSomeRoles = (
  user: MyUser | UserGet | undefined,
  roles: AppRoleType[],
) => {
  return user ? roles?.some((role) => user.roles?.includes(role)) : false;
};

export const hasSomeAppPermissions = (
  user: MyUser | undefined,
  permissions: PermissionType[],
) => {
  return user
    ? permissions?.some(
        (permission) => user.permissions?.app.includes(permission),
      )
    : false;
};

export const hasAllRoles = (
  user: MyUser | UserGet | undefined,
  roles: AppRoleType[],
) => {
  return user ? roles?.every((role) => user.roles?.includes(role)) : false;
};

export const convertFeatureToMultiPolygonFeature = (p: FieldBoundary) => {
  if (p.geometry.type === "MultiPolygon") {
    return p as FeatureGeoJSON<MultiPolygon>;
  }
  return {
    type: "Feature",
    geometry: {
      type: "MultiPolygon",
      coordinates: [p.geometry.coordinates],
    },
    properties: p.properties,
  } as FeatureGeoJSON<MultiPolygon>;
};

export const featureCollectionFromFieldList = (
  fields: FieldWithCurrentBoundary[] | undefined,
): FeatureCollection => {
  return {
    type: "FeatureCollection",
    features: (fields || []).map(featureFromField).filter(filterNullUndefined),
  };
};

/**
 * Take a field object from our typed API and convert it to something compatible
 * with standard GeoJSON Feature objects. This is useful for passing field info
 * to Mapbox and turf.js.
 * @param field
 * @returns
 */
export const featureFromField = (
  field: FieldWithCurrentBoundary | undefined,
): FieldBoundary | undefined => {
  return (
    // Field.currentBoundary is type FieldBoundaryGet, which only includes
    // geometry, so we need to add the id and name properties
    field?.currentBoundary?.geometry && {
      type: "Feature",
      id: field.id, // id field is also filled by MapBox Draw library
      geometry: {
        ...field?.currentBoundary?.geometry,
        // regen-web's definition of bbox includes null, which GeoJSON does not
        bbox: field?.currentBoundary?.geometry.bbox ?? undefined,
      },
      properties: {
        field_id: field.id,
        name: field.name,
      },
    }
  );
};

// Coerce a regen-api-ts-client Feature object to a GeoJSON Feature object
export const geojsonFeatureFromRegenFeature = (
  feature: Feature,
): FeatureGeoJSON<Polygon | MultiPolygon> | undefined => {
  if (
    !(
      feature?.geometry?.type === "MultiPolygon" ||
      feature?.geometry?.type === "Polygon"
    )
  ) {
    return undefined;
  }
  return feature?.geometry
    ? {
        id: feature.id || undefined,
        type: "Feature",
        geometry: {
          ...feature.geometry,
          // regen-web's definition of bbox includes null, which GeoJSON does not
          bbox: feature.geometry.bbox ?? undefined,
        },
        properties: feature.properties,
      }
    : undefined;
};

// Follows the Fair password policy from here: https://auth0.com/docs/authenticate/database-connections/password-strength
export const isValidPassword = (password: string) => {
  const isValidLength = password.length >= 8;

  // Check if password contains at least one uppercase letter
  const containsUppercase = /[A-Z]/.test(password);

  // Check if password contains at least one lowercase letter
  const containsLowercase = /[a-z]/.test(password);

  // Check if password contains at least one digit
  const containsDigit = /\d/.test(password);

  return (
    isValidLength && containsUppercase && containsLowercase && containsDigit
  );
};

export const validatePassword = (password: string) => {
  if (!password) {
    return "Password is required";
  }
  if (!isValidPassword(password)) {
    return "Password must be 8 characters including a lower-case letter, an upper-case letter, and a number.";
  }

  return undefined;
};

export const validateFormValue = (value: FormValue, errorMessage?: string) => {
  if (!value) {
    return errorMessage || "This field is required";
  }
};

export const createValidationErrorObject = <T>(
  requiredFields: Partial<keyof T>[],
  formState: T,
) => {
  return requiredFields.reduce((acc, field) => {
    const value = formState[field];
    return { ...acc, [field]: validateFormValue(value as FormValue) };
  }, {} as FormErrors);
};

export const mergeFieldBoundaries = (
  fieldBoundaries: FieldBoundary[] = [],
): MultiPolygon | undefined => {
  let unionedBoundaries;

  if (fieldBoundaries.length > 1) {
    unionedBoundaries = union({
      type: "FeatureCollection",
      features: fieldBoundaries,
    });
  } else if (fieldBoundaries.length == 1) {
    unionedBoundaries = fieldBoundaries[0];
  }

  if (unionedBoundaries) {
    if (unionedBoundaries.geometry.type === "MultiPolygon") {
      return unionedBoundaries.geometry;
    } else {
      // translate a Polygon to a MultiPolygon
      return {
        type: "MultiPolygon",
        coordinates: [unionedBoundaries.geometry.coordinates],
      };
    }
  }
};

export const FILENAME_REG_EXP = RegExp(/attachment; filename="(.*?)"/);

export const getResponseFilename = (response: Response) => {
  return (
    FILENAME_REG_EXP.exec(
      response.headers.get("Content-Disposition") || "",
    )?.[1] || ""
  );
};

export const downloadFile = (blob: Blob, fileName: string) => {
  const blobUrl = window.URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();

  window.URL.revokeObjectURL(blobUrl);
  document.body.removeChild(anchor);
};

export const getFileNameFromPath = (path: string) => {
  const parts = path.split("/");
  return parts[parts.length - 1];
};

export const roundToNearestTenth = (num: number) => {
  return Math.round(num * 10) / 10;
};
export const formatAcres = (num?: number | null): string | number => {
  if (!num) {
    return "-";
  }

  const formatNumber = (
    number: number,
    divisor: number,
    suffix: string,
  ): string => {
    const multiple = number / divisor;
    const roundedMultiple = roundToNearestTenth(multiple);
    return `${
      Number.isInteger(roundedMultiple)
        ? roundedMultiple.toFixed(0)
        : roundedMultiple.toFixed(1)
    }${suffix}`;
  };

  if (num >= 1e6) {
    return formatNumber(num, 1e6, "M");
  }

  if (num >= 1e3) {
    return formatNumber(num, 1e3, "K");
  }

  return roundToNearestTenth(num);
};

export const isValidUrl = (url: string) => {
  const pattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
      "((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.)+[a-zA-Z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-zA-Z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-zA-Z\\d_]*)?$",
    "i",
  ); // fragment locator
  return !!pattern.test(url);
};
export const validateUrl = (url: string) => {
  if (!isValidUrl(url)) {
    return "Please enter a valid URL.";
  }

  return undefined;
};

export const convertDateToQuarter = (dateStr?: string | null) => {
  if (!dateStr) {
    return "-";
  }

  const month = parseInt(dateStr.split("-")[1], 10);

  let quarter: number;
  if (month <= 3) {
    quarter = 1;
  } else if (month >= 4 && month <= 6) {
    quarter = 2;
  } else if (month >= 7 && month <= 9) {
    quarter = 3;
  } else {
    quarter = 4;
  }

  const year = dateStr.split("-")[0];

  return `${year} Q${quarter}`;
};

/**
 * Create a case-insensitive sort function for objects, based on the passed
 * key name.
 * @param key the object key to sort by i.e. "name"
 * @returns
 */
export const sortCaseInsensitiveFactory = <
  // the key should be a string - lets us create a generic to enforce the key's
  // existence on the objects to sort
  ObjKey extends string,
  // The objects (a, b) should have a key that matches the key passed to the
  // factory function, and that property itself should be a string
  Obj extends { [K in ObjKey]: string },
>(
  key: ObjKey,
) => {
  return (a: Obj, b: Obj) => {
    return a[key].localeCompare(b[key], undefined, { sensitivity: "base" });
  };
};

export const envName = (endpoint: string): string => {
  switch (true) {
    case endpoint.endsWith("api.vayda.ag"):
      return "prod";
    case endpoint.endsWith("api.staging.vayda.dev"):
      return "staging";
    default:
      return "dev";
  }
};

// Convert a date string to a UTC date
export const strToUTCDate = (dateString: string): Date => {
  // dateString is in UTC time (matches our BE data) but localDate becomes local time when
  // converted from string to date. The date picker only accepts Date input.
  const localDate = dateString ? new Date(dateString) : new Date();

  // Date is currently in local time so reset the hours (and days if applicable) so that
  // the date picker will display the correct dates for which we have data.
  const utcHours = localDate.getUTCHours();
  const utcDate = localDate.getUTCDate();
  const utcMonth = localDate.getUTCMonth();
  const utcYear = localDate.getUTCFullYear();
  localDate.setHours(utcHours);
  localDate.setDate(utcDate);
  localDate.setMonth(utcMonth);
  localDate.setFullYear(utcYear);
  return localDate;
};
